import { useCallback, useRef, useState } from 'react';

/**
 * Guard an action against over-execution from rapid clicks.
 *
 * `run(action)` ignores re-entry while a prior invocation is still in flight.
 * The synchronous ref blocks a second click *before* React re-renders (so a
 * spammed button can't fire twice in the same frame), while `pending` drives
 * the button's disabled state. Both reset when the action settles, so an action
 * that failed (e.g. a validation error that keeps a form open) can be retried.
 */
export function useSingleFlight(): {
  pending: boolean;
  run: (action?: () => void | Promise<void>) => Promise<void>;
} {
  const inFlight = useRef(false);
  const [pending, setPending] = useState(false);

  const run = useCallback(async (action?: () => void | Promise<void>) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setPending(true);
    try {
      await action?.();
    } finally {
      inFlight.current = false;
      setPending(false);
    }
  }, []);

  return { pending, run };
}
